import React from 'react';
import { Title, Loader, ReloadIconButton, ClearFilters } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  Box,
  Button,
  Chip,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { ApiService } from '../../hooks';
import { extractApiErrorMessage, notifications } from '../../utils';
import { CreateUpdateSlaughterhouseModal } from '../../components';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel } from '../../api/HealthSystemApi';
import { useNavigate } from 'react-router-dom';

const SlaughterhousesPage: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [createUpdateDialogOpen, setCreateUpdateDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<'create' | 'update'>('create');
  const [selectedSlaughterhouse, setSelectedSlaughterhouse] =
    React.useState<HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel | null>(null);

  const [slaughterhouses, setSlaughterhouses] = React.useState<
    HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel[]
  >([]);

  const [searchText, setSearchText] = React.useState('');

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadSlaughterhouses = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.slaughterhouses
      .slaughterhousesGetAll({ Page: searchPage, Size: rowsPerPage, SearchTerm: searchText })
      .then((result) => {
        setSlaughterhouses(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setSlaughterhouses([]);
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadSlaughterhouses();
      loadData();
    }
  }, [loaded]);

  const handleCreateUpdateModal = (result: boolean | null) => {
    setCreateUpdateDialogOpen(false);
    if (result) {
      setLoaded(false);
    }
  };

  const handleCreateClick = () => {
    setSelectedSlaughterhouse(null);
    setDialogMode('create');
    setCreateUpdateDialogOpen(true);
  };

  const handleEditClick = (slaughterhouse: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel) => {
    setSelectedSlaughterhouse(slaughterhouse);
    setDialogMode('update');
    setCreateUpdateDialogOpen(true);
  };

  const handleViewClick = (slaughterhouse: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel) => {
    navigate(`/slaughterhouse-management/${slaughterhouse.id}`);
  };

  const handleRowClick = (slaughterhouse: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel) => {
    navigate(`/slaughterhouse-management/${slaughterhouse.id}`);
  };

  const handleSearch = () => {
    setPage(0);
    setLoaded(false);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setPage(0);
    setLoaded(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex' }}>
            <Icon color="primary" fontSize="large">
              {'factory'}
            </Icon>
            <Box sx={{ ml: 1, pt: 0.5 }}>
              <Title>{t('f-slaughterhouses')}</Title>
            </Box>
            <Box sx={{ ml: 1 }}>
              <Chip label={total} variant="outlined" />
            </Box>
          </Box>
          <Box>
            <ReloadIconButton action={() => setLoaded(false)} />
            <Tooltip arrow title={t('f-com-create-new')}>
              <IconButton
                color="primary"
                size="medium"
                aria-label={t('f-com-create-new')}
                onClick={handleCreateClick}
                sx={V4_ICON_BUTTON}
              >
                <Icon>add_circle</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={styles.p16}>
          <Grid container spacing={1}>
            <Grid item md={8} sm={6} xs={12}>
              <TextField
                fullWidth
                id="searchText"
                label={t('f-com-search')}
                variant="outlined"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Grid>
            <Grid item md={2} sm={3} xs={6}>
              <ClearFilters onClick={handleClearFilters} />
            </Grid>
            <Grid item md={2} sm={3} xs={6}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="small"
                startIcon={<Icon>search</Icon>}
                onClick={handleSearch}
              >
                {t('f-com-search')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      <Grid item xs={12}>
        <Paper variant="outlined">
          <Table size="small" sx={V4_TABLE}>
            <TableHead sx={styles.tableHeader}>
              <TableRow>
                <TableCell>{t('f-slaughterhouse-name')}</TableCell>
                <TableCell>{t('f-slaughterhouse-code')}</TableCell>
                <TableCell align="center">{t('f-com-active')}</TableCell>
                <TableCell align="center">{t('f-com-actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loaded && slaughterhouses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography>{t('f-no-data')}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {loaded &&
                slaughterhouses.map((slaughterhouse) => (
                  <TableRow
                    key={slaughterhouse.id}
                    hover
                    sx={styles.tableRowClickable}
                    onClick={() => handleRowClick(slaughterhouse)}
                  >
                    <TableCell>{slaughterhouse.name}</TableCell>
                    <TableCell>{slaughterhouse.code || '-'}</TableCell>
                    <TableCell align="center">
                      {slaughterhouse.isActive ? (
                        <Chip label={t('f-com-active')} color="primary" size="small" />
                      ) : (
                        <Chip label={t('f-com-inactive')} color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip arrow title={t('f-com-view')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClick(slaughterhouse);
                          }}
                          sx={V4_ICON_BUTTON_SMALL}
                        >
                          <Icon>visibility</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow title={t('f-com-edit')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(slaughterhouse);
                          }}
                          sx={V4_ICON_BUTTON_SMALL}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                  colSpan={4}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Paper>
      </Grid>

      <CreateUpdateSlaughterhouseModal
        opened={createUpdateDialogOpen}
        onModalAction={handleCreateUpdateModal}
        mode={dialogMode}
        slaughterhouse={selectedSlaughterhouse}
      />
    </Grid>
  );
};

export default SlaughterhousesPage;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    p16: {
      padding: theme.spacing(2)
    },
    h56: {
      height: 56
    },
    tableRowClickable: {
      cursor: 'pointer'
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    centred: {
      alignItems: 'center'
    }
  }) as const;
